import { KIT_SOURCE_URL_PREFIX } from '../../appKit/host';

export interface HtmlSandboxPolicy {
  /** Bare host names, each allowed over https; empty means no network at all. */
  readonly allowedHosts: readonly string[];
}

export interface SandboxKit {
  readonly version: number;
  readonly source: string;
}

export interface SandboxDocumentOptions {
  readonly hostOrigin?: string;
  /** The page never starts a run, so the kit leaves out what only a run needs. */
  readonly agentFree?: boolean;
}

/**
 * The frame's CSP. Inline script, eval and WebAssembly are allowed because
 * generated pages are single files that lean on all three; what is locked down
 * is where anything may come from. `webrtc` is honoured where browsers support
 * it; the kit also removes the WebRTC constructors.
 */
export const sandboxCsp = (policy: HtmlSandboxPolicy): string => {
  const hosts = policy.allowedHosts.map((host) => `https://${host}`);
  const from = (...sources: string[]) => [...sources, ...hosts].join(' ');
  return [
    "default-src 'none'",
    `script-src ${from("'unsafe-inline'", "'unsafe-eval'", "'wasm-unsafe-eval'", 'blob:', 'data:')}`,
    `style-src ${from("'unsafe-inline'", 'data:', 'blob:')}`,
    `font-src ${from('data:', 'blob:')}`,
    `img-src ${from('data:', 'blob:')}`,
    `media-src ${from('data:', 'blob:')}`,
    `connect-src ${from('data:', 'blob:')}`,
    'worker-src blob: data:',
    "frame-src 'none'",
    "form-action 'none'",
    "base-uri 'none'",
    "webrtc 'block'",
  ].join('; ');
};

const doctypeOf = (doc: Document): string =>
  doc.doctype ? new XMLSerializer().serializeToString(doc.doctype) : '<!DOCTYPE html>';

/**
 * The page as the frame loads it: the CSP and the App Kit are the first
 * children of `<head>`, so both are in force before anything of the page runs.
 * The kit is told the app's origin, the only one it talks to, and named by a
 * sourceURL so its errors are told apart from the page's. DOMParser never
 * executes what it parses, which is what makes rewriting untrusted markup safe.
 */
export const buildSandboxDocument = (
  html: string,
  policy: HtmlSandboxPolicy,
  kit: SandboxKit,
  { hostOrigin = window.location.origin, agentFree = false }: SandboxDocumentOptions = {},
): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const csp = doc.createElement('meta');
  csp.httpEquiv = 'Content-Security-Policy';
  csp.content = sandboxCsp(policy);
  const script = doc.createElement('script');
  script.textContent = `${kit.source}\n//# sourceURL=${KIT_SOURCE_URL_PREFIX}-v${kit.version}.js`;
  script.dataset.hostOrigin = hostOrigin;
  script.toggleAttribute('data-agent-free', agentFree);
  doc.head.prepend(csp, script);
  return `${doctypeOf(doc)}\n${doc.documentElement.outerHTML}`;
};
